import {AppConfigService} from "../config/app-config.service";

/**
 * Resolves the Sentry environment tag from runtime config.
 *
 * Fetches the build-appropriate config asset — the same file `AppConfigService`
 * loads, via `AppConfigService.resolveConfigUrl()` — and returns the
 * `environment` string it contains. Falls back to `fallback` on any error:
 * network failure, non-ok HTTP status, missing key, or empty value.
 *
 * The injectable `fetchFn` parameter (defaulting to the global `fetch`)
 * makes the function unit-testable without module-level mocking.
 */
export async function resolveSentryEnvironment(
  fallback: string,
  fetchFn: typeof fetch = fetch,
): Promise<string> {
  try {
    const response = await fetchFn(AppConfigService.resolveConfigUrl());
    if (!response.ok) {
      return fallback;
    }
    const config = await response.json();
    const env = config?.environment;
    if (env && typeof env === 'string' && env.trim().length > 0) {
      return env;
    }
    return fallback;
  } catch {
    return fallback;
  }
}

/**
 * Whether Sentry should initialise for the given resolved environment.
 * Enabled for the deployed environments only — local dev (`dev` /
 * `development`) stays off. The allowlist tokens match the per-environment
 * `environment` values supplied by the hec-infra Helm values.
 */
export function shouldEnableSentry(env: string): boolean {
  return ['staging', 'pre-prod', 'production'].includes(env);
}
